<template>
  <div ref="containerRef" class="product-highlights-container">
    <div
      class="product-highlights"
      :class="{
        'has-low-highlights-amount': props.hasLowHighlightsAmount,
        'has-middle-highlights-amount': props.hasMiddleHighlightsAmount,
      }"
      :tabindex="isDesktop ? -1 : 0"
      role="region"
      aria-label="Product highlights"
    >
      <HighlightTile
        v-for="(highlight, index) in props.highlights"
        :key="highlight.name"
        class="highlight-tile"
        v-bind="highlight"
        :is-row-expanded="rowStates[index % numberOfRows]?.isExpanded || false"
        @expand-request="handleExpandRequest(index)"
        @collapse-request="handleCollapseRequest(index)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import HighlightTile from './HighlightTile.vue';

const props = defineProps<{
  highlights: {
    name: string;
    value: string;
    facetDisplayName: string;
  }[];
  hasLowHighlightsAmount?: boolean;
  hasMiddleHighlightsAmount?: boolean;
}>();

const emit = defineEmits(['height-change']);

const containerRef = ref(null);
useResizeObserver(containerRef, (entries) => {
  const entry = entries[0];
  const { height } = entry.contentRect;
  emit('height-change', height);
});

const { isDesktop, isDesktopMedium } = useDeviceType();

const numberOfRows = computed(() => {
  if (isDesktop.value && !isDesktopMedium.value) {
    return 2;
  }
  if (isDesktop.value) {
    return 4;
  }
  return 2;
});

const rowStates = reactive([{ isExpanded: false }, { isExpanded: false }, { isExpanded: false }, { isExpanded: false }]);

const handleExpandRequest = (itemIndex: number) => {
  const rowIndex = itemIndex % numberOfRows.value;
  if (rowStates[rowIndex]) {
    rowStates[rowIndex].isExpanded = true;
  }
};

const handleCollapseRequest = (itemIndex: number) => {
  const rowIndex = itemIndex % numberOfRows.value;
  if (rowStates[rowIndex]) {
    rowStates[rowIndex].isExpanded = false;
  }
};
</script>

<style lang="scss" scoped>
.product-highlights-container {
  padding-left: rem-calc(16);
  --gap: calc(2rem / 2);
  position: relative;
  overflow: visible;
  width: calc(100% + var(--gap) * 2);
  left: calc(-1 * var(--gap));
  right: var(--gap);
}

@include breakpoint(large up) {
  .product-highlights-container {
    padding-left: rem-calc(24);
    right: auto;
    left: auto;
  }
}

.product-highlights {
  display: grid;
  column-gap: rem-calc(16);
  row-gap: rem-calc(40);
  margin-bottom: rem-calc(24);
  grid-auto-flow: column;
  grid-template-rows: repeat(2, minmax(rem-calc(40), auto));
  grid-template-columns: repeat(4, 36%);
  overflow-x: scroll;
  width: 100%;
  box-sizing: border-box;

  -ms-overflow-style: none;
  scrollbar-width: none;
}

@include breakpoint(small only) {
  .product-highlights {
    grid-template-columns: repeat(4, 40%);
  }
  .product-highlights.has-low-highlights-amount {
    grid-template-columns: repeat(2, 45%);
  }

  .product-highlights.has-middle-highlights-amount {
    grid-template-columns: repeat(3, 40%);
  }
}

@include breakpoint(medium only) {
  .product-highlights {
    grid-template-columns: repeat(4, rem-calc(163));
  }

  .product-highlights.has-low-highlights-amount {
    grid-template-columns: repeat(2, rem-calc(163));
  }

  .product-highlights.has-middle-highlights-amount {
    grid-template-columns: repeat(3, rem-calc(163));
  }
}

.product-highlights::-webkit-scrollbar {
  display: none;
}

@include breakpoint(large up) {
  .product-highlights {
    column-gap: rem-calc(24);
    row-gap: rem-calc(48);
    margin-bottom: 0;
    padding-bottom: rem-calc(48);
  }
}

@include breakpoint(large only) {
  .product-highlights {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(4, minmax(rem-calc(40), auto));
    grid-auto-flow: column;
  }

  .product-highlights.has-low-highlights-amount {
    grid-template-rows: repeat(2, minmax(rem-calc(40), 1fr));
  }

  .product-highlights.has-middle-highlights-amount {
    grid-template-rows: repeat(3, minmax(rem-calc(40), 1fr));
  }
}

@include breakpoint(mlarge up) {
  .product-highlights {
    grid-auto-flow: column;
    grid-template-rows: repeat(2, minmax(rem-calc(40), auto));
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>
